import { Component, ChangeDetectionStrategy } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { ValidationProblem, Order, OrderManagerService, ValidationService, OrderGroupsService, ValidatedOrder, eOrderType, OrderRepositoryService, SlideDownAnimation, Utilities, instantValidationThrottle } from 'reg-hub-common';
import { BehaviorSubject, first, forkJoin, map, switchMap, takeUntil } from 'rxjs';

export interface OrderData {
  id: string,
  data: {
    order: Order,
    errors: ValidationProblem[] | null | undefined,
    serialNumberControl?: AbstractControl,
    serialNumberErrors$?: BehaviorSubject<string | null | undefined>
    firstNameControl?: AbstractControl,
    firstNameErrors$?: BehaviorSubject<string | null | undefined>
    middleNameControl?: AbstractControl,
    middleNameErrors$?: BehaviorSubject<string | null | undefined>
    lastNameControl?: AbstractControl,
    lastNameErrors$?: BehaviorSubject<string | null | undefined>
    busNameControl?: AbstractControl,
    busNameErrors$?: BehaviorSubject<string | null | undefined>
    dateOfBirthControl?: AbstractControl,
    dateOfBirthErrors$?: BehaviorSubject<string | null | undefined>
  }
}

@Component({
    selector: 'app-multi-search-criteria-validation',
    templateUrl: './multi-search-criteria-validation.component.html',
    styleUrls: ['./multi-search-criteria-validation.component.css'],
    animations: [SlideDownAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiSearchCriteriaValidationComponent extends SteppedComponent {
  protected ordersForm!: FormGroup;

  protected data: OrderData[] = [];

  protected validating = false;
  protected saving = false;

  constructor(
    orderManagerService: OrderManagerService,
    validationService: ValidationService,
    private orderGroupsService: OrderGroupsService,
    private formBuilder: FormBuilder,
    private ordersRepository: OrderRepositoryService) {
    super(orderManagerService, validationService);
  }

  override ngOnInit(): void {
    this.shouldSaveOrderOnStepChange = false;
    this.validating = true;

    super.ngOnInit();

    this.ordersForm = this.formBuilder.group({
      orders: this.formBuilder.array([])
    })

    this.saving$.pipe(
      takeUntil(this.onDestroy$),
      first(val => val === false),
      switchMap(() => this.orderGroupsService.getOrderGroupOrdersWithValidation(this.order.orderGroup?.id ?? '', 100, new HttpParams().append('isChildSearch', true))),
      map((resources: ValidatedOrder[]) => resources
        .filter(r => (r.validation.errors?.length ?? 0) > 0 && r.resource.orderTypeID !== eOrderType.assetMultiSearch && r.resource.orderTypeID !== eOrderType.debtorMultiSearch)
        .sort(Utilities.sortByJurisdiction(r => r.resource.jurisdiction))),
      map((resources: ValidatedOrder[]) => {
        return resources.map(r => {
          return {
            id: r.resource.id,
            data: {
              order: r.resource,
              errors: this.mapPathsToLowerCase(r.validation.errors)
            }
          } as OrderData;
        })
      }))
      .subscribe((data: OrderData[]) => {
        this.data = data;

        if (this.data.length === 0) {
          this.validating = false;
        } else {
          const ordersArray = this.ordersForm.get('orders') as FormArray;

          this.data.forEach(entry => {
            let orderFormGroup: FormGroup | undefined;

            if (entry.data.order.orderTypeID === eOrderType.assetSearch) {
              const serialNumberControl = this.formBuilder.control(entry.data.order.assets?.at(0)?.serialNumber);
              entry.data.serialNumberControl = serialNumberControl;
              entry.data.serialNumberErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('serialnumber'))?.at(0)?.userFriendlyMessage);

              orderFormGroup = this.formBuilder.group({
                serialNumber: serialNumberControl
              });
            } else if(entry.data.order.orderTypeID === eOrderType.debtorSearch) {
              const firstNameControl = this.formBuilder.control(entry.data.order.parties?.at(0)?.firstName);
              entry.data.firstNameControl = firstNameControl;
              entry.data.firstNameErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('name'))?.at(0)?.userFriendlyMessage);

              const middleNameControl = this.formBuilder.control(entry.data.order.parties?.at(0)?.middleName);
              entry.data.middleNameControl = middleNameControl;
              entry.data.middleNameErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('middlename'))?.at(0)?.userFriendlyMessage);

              const lastNameControl = this.formBuilder.control(entry.data.order.parties?.at(0)?.lastName);
              entry.data.lastNameControl = lastNameControl;
              entry.data.lastNameErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('name'))?.at(0)?.userFriendlyMessage);

              const busNameControl = this.formBuilder.control(entry.data.order.parties?.at(0)?.busName);
              entry.data.busNameControl = busNameControl;
              entry.data.busNameErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('busname'))?.at(0)?.userFriendlyMessage);

              const dateOfBirthControl = this.formBuilder.control(entry.data.order.parties?.at(0)?.dateOfBirth);
              entry.data.dateOfBirthControl = dateOfBirthControl;
              entry.data.dateOfBirthErrors$ = new BehaviorSubject<string | null | undefined>(entry.data.errors?.filter(err => err.path?.endsWith('dateofbirth'))?.at(0)?.userFriendlyMessage);

              orderFormGroup = this.formBuilder.group({
                firstName: firstNameControl,
                middleName: middleNameControl,
                lastName: lastNameControl,
                busName: busNameControl,
                dateOfBirth: dateOfBirthControl
              });
            }

            // For instant validation
            orderFormGroup?.valueChanges
              .pipe(
                takeUntil(this.onDestroy$),
                instantValidationThrottle())
              .subscribe((form: any) => {
                if (this.order.orderTypeID === eOrderType.assetMultiSearch) {
                  const serialNumber = form.serialNumber;
                  entry.data.order.assets!.at(0)!.serialNumber = serialNumber;
                } else if (this.order.orderTypeID === eOrderType.debtorMultiSearch) {
                  entry.data.order.parties!.at(0)!.firstName = form.firstName;
                  entry.data.order.parties!.at(0)!.middleName = form.middleName;
                  entry.data.order.parties!.at(0)!.lastName = form.lastName;
                  entry.data.order.parties!.at(0)!.busName = form.busName;
                  entry.data.order.parties!.at(0)!.dateOfBirth = form.dateOfBirth;
                }

                this.validationService.validate(entry.data.order)
                  .pipe(map(results => this.mapPathsToLowerCase(results.errors)))
                  .subscribe(results => {
                    entry.data.errors = results;
                    entry.data.serialNumberErrors$?.next(results?.filter(err => err.path?.endsWith('serialnumber'))?.at(0)?.userFriendlyMessage);
                    entry.data.firstNameErrors$?.next(results?.filter(err => err.path?.endsWith('firstname'))?.at(0)?.userFriendlyMessage);
                    entry.data.middleNameErrors$?.next(results?.filter(err => err.path?.endsWith('middlename'))?.at(0)?.userFriendlyMessage);
                    entry.data.lastNameErrors$?.next(results?.filter(err => err.path?.endsWith('lastname'))?.at(0)?.userFriendlyMessage);
                    entry.data.busNameErrors$?.next(results?.filter(err => err.path?.endsWith('busname'))?.at(0)?.userFriendlyMessage);
                    entry.data.dateOfBirthErrors$?.next(results?.filter(err => err.path?.endsWith('dateofbirth'))?.at(0)?.userFriendlyMessage);
                  });
              });

            ordersArray.setControl(this.data.indexOf(entry), orderFormGroup);
          });

          this.validating = false;
        }
      })
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return this.data.some(entry => (entry.data.errors?.length ?? 0) > 0) ? StepValidationState.ValidationError : StepValidationState.ValidationSuccess;
  }

  public shouldShowForm() {
    const ordersFormArray = this.ordersForm.get('orders') as FormArray;
    return this.data.length > 0 && this.data.length === ordersFormArray.length;
  }

  public getSerialNumberControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.serialNumberControl;
  }

  public getFirstNameControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.firstNameControl;
  }

  public getMiddleNameControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.middleNameControl;
  }

  public getLastNameControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.lastNameControl;
  }

  public getBusNameControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.busNameControl;
  }

  public getDateOfBirthControl(id: string) {
    return this.data.find(entry => entry.id === id)?.data.dateOfBirthControl;
  }

  public getSerialNumberErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.serialNumberErrors$!
  }

  public getFirstNameErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.firstNameErrors$!
  }

  public getMiddleNameErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.middleNameErrors$!
  }

  public getLastNameErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.lastNameErrors$!
  }

  public getBusNameErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.busNameErrors$!
  }

  public getDateOfBirthErrorSubject(id: string): BehaviorSubject<string | null | undefined> {
    return this.data.find(entry => entry.id === id)?.data.dateOfBirthErrors$!
  }

  override onSaving(): void {
    //Need to use order manager saving observable as we don't have access to the stepper one here 
    //and we potentially may need to save all the orders
    this.orderManager.saving$.next(true);
    forkJoin(this.data.map(entry => this.ordersRepository.updateOrder(`orders/${entry.data.order.id}`, entry.data.order)))
      .subscribe({ complete: () => this.orderManager.saving$.next(false)});
  }
}
